/**
 * Pure pattern-match redirect rules + lookup helper. Split off from
 * `/lib/redirects.js` so it can be unit-tested without pulling in axios
 * (which jest can't transform as an ES module without extra config).
 *
 * `findRedirect()` and `PATTERN_REDIRECTS` are re-exported from
 * `redirects.js` so existing call sites continue to import from the
 * same path.
 */
import { getStateBySlug, getStateByCode } from "./states";

/**
 * AI crawlers (ChatGPT / Perplexity / Bard) invent project-keyword
 * variants that don't match our canonical slugs. Each entry below maps
 * a hallucinated path prefix to the corresponding canonical statePathBase
 * (the actual slug we use in /lib/calculators.js).
 *
 * Discovered patterns (from production /admin/not-found 2026-06):
 *   - /deck-construction-cost-{state} → canonical /deck-cost-{state}
 *     (200+ hits, AI prefers verb form "construction" over our "cost")
 *   - /basement-finishing-cost-in-{state} → /basement-finishing-cost-{state}
 *     (47 hits — same "in-" residue we saw earlier on /deck-cost-in-*)
 *   - /landscaping-cost-{state} → /guides/landscaping-cost-{state}-2026
 *     (57 hits — AI strips our /guides/ prefix + -2026 suffix because
 *     the cost-by-state guide format mirrors our calculator URLs)
 *   - /hardscape-cost-{state} → /guides/hardscape-cost-{state}-2026
 *     (55 hits — same /guides/ + -2026 stripping as landscaping)
 *
 * Proactively defended patterns (the same "construction" / "installation" /
 * "remodeling" verb-form hallucinations applied to every other project
 * that has state landing pages):
 *   - bathroom-construction-cost / bathroom-remodeling-cost
 *     → /bathroom-remodel-cost-{state}
 *   - kitchen-construction-cost / kitchen-remodeling-cost
 *     → /kitchen-remodel-cost-{state}
 *   - roof-construction-cost / roof-installation-cost
 *     → /roofing-cost-{state}
 *   - window-installation-cost
 *     → /window-replacement-cost-{state}
 *   - solar-installation-cost
 *     → /solar-panel-cost-{state}
 *   - flooring-installation-cost
 *     → /flooring-cost-{state}
 *   - basement-renovation-cost / basement-remodel-cost
 *     → /basement-finishing-cost-{state}
 *   - fence-construction-cost
 *     → /fence-installation-cost-{state}
 *   - pool-construction-cost / pool-build-cost
 *     → /pool-installation-cost-{state}
 *   - painting-service-cost / house-painting-cost
 *     → /painting-cost-{state}
 *
 * Each entry validates the captured slug via getStateBySlug — invalid
 * slugs (Narnia, Mordor) return null so the visitor sees the 404 rather
 * than getting misrouted.
 *
 * Add new entries as `/admin/not-found` surfaces them (or via the
 * /admin SEO Checklist tab's "Suggested Pattern Redirects" card —
 * see backend/routes/admin.py admin_suggest_redirect_patterns).
 */
const HALLUCINATION_MAP = [
  // [hallucinated path prefix (after leading /), canonical statePathBase, optional canonical suffix]
  // The optional 3rd element is appended to the rewritten URL — used for
  // guide-style targets that need a `-2026` suffix tail.
  ["deck-construction-cost",     "/deck-cost"],
  ["deck-installation-cost",     "/deck-cost"],
  // /deck-cost-in-{state} — residue from a buggy 2026-02 iteration that
  // briefly redirected to this wrong target. Surfaced by the discovery
  // cron with 6 hits across 5 states; one-line entry closes the loop so
  // any visitor hitting a stale link gets to canonical /deck-cost-{state}.
  ["deck-cost-in",               "/deck-cost"],
  // /basement-finishing-cost-in-{state} — identical "in-" residue pattern.
  // Discovered 2026-06 with 47 hits across 47 states.
  ["basement-finishing-cost-in", "/basement-finishing-cost"],
  ["bathroom-construction-cost", "/bathroom-remodel-cost"],
  ["bathroom-remodeling-cost",   "/bathroom-remodel-cost"],
  ["kitchen-construction-cost",  "/kitchen-remodel-cost"],
  ["kitchen-remodeling-cost",    "/kitchen-remodel-cost"],
  ["roof-construction-cost",     "/roofing-cost"],
  ["roof-installation-cost",     "/roofing-cost"],
  ["roof-replacement-cost",      "/roofing-cost"],
  ["window-installation-cost",   "/window-replacement-cost"],
  ["solar-installation-cost",    "/solar-panel-cost"],
  ["solar-system-cost",          "/solar-panel-cost"],
  ["flooring-installation-cost", "/flooring-cost"],
  ["flooring-replacement-cost",  "/flooring-cost"],
  ["basement-renovation-cost",   "/basement-finishing-cost"],
  ["basement-remodel-cost",      "/basement-finishing-cost"],
  ["basement-finish-cost",       "/basement-finishing-cost"],
  ["fence-construction-cost",    "/fence-installation-cost"],
  ["fencing-cost",               "/fence-installation-cost"],
  ["pool-construction-cost",     "/pool-installation-cost"],
  ["pool-build-cost",            "/pool-installation-cost"],
  ["painting-service-cost",      "/painting-cost"],
  ["house-painting-cost",        "/painting-cost"],
  ["interior-painting-cost",     "/painting-cost"],
  // Note: landscaping-cost and hardscape-cost were briefly routed to the
  // /guides/{slug}-2026 editorial pages (2026-06 hallucination cycle). In
  // pass-47 we promoted both to first-class calculators with their own
  // CalculatorPage + 50 state landings, so these prefixes now resolve
  // natively at /landscaping-cost-{state} and /hardscape-cost-{state} —
  // no redirect needed.
];

// Build the regex/build callbacks from the declarative map above. Keeps
// adding new patterns to a one-liner instead of writing the regex by hand.
export const PATTERN_REDIRECTS = HALLUCINATION_MAP.map(([prefix, canonical, suffix = ""]) => ({
  regex: new RegExp(`^/${prefix}-([a-z][a-z-]+)$`, "i"),
  build: (m) => {
    const slug = m[1].toLowerCase();
    return getStateBySlug(slug) ? `${canonical}-${slug}${suffix}` : null;
  },
  // Surface raw metadata for the admin "suggested patterns" UI so the
  // panel can render human-readable explanations of each active rule.
  meta: { hallucinatedPrefix: prefix, canonicalPrefix: canonical, canonicalSuffix: suffix },
}));

/**
 * PATH_PATTERN_REDIRECTS — full-path regex rewrites for hallucinations
 * that don't fit the `/{prefix}-{state}` shape. Distinct from
 * PATTERN_REDIRECTS (above) which assumes a single state slug at the
 * tail of the path.
 *
 * Each entry returns a literal target path (already rewritten) so the
 * caller can return it unchanged.
 *
 * Why we need both layers:
 *   - PATTERN_REDIRECTS handles "AI-thinks-deck-construction-cost-is-the-slug"
 *     style errors (1 prefix × 50 states = 50 hallucinated URLs).
 *   - PATH_PATTERN_REDIRECTS handles segment-shape errors like
 *     "/permit-cost/{project}-permit-{state}" → "/permit-cost/{project}-in-{state}"
 *     (11 projects × 50 states = 550 hallucinated URLs from one bad
 *     slug pattern).
 */
export const PATH_PATTERN_REDIRECTS = [
  // /permit-cost/{project}-permit-{state} → /permit-cost/{project}-in-{state}
  // AI tools (esp. GPT-3.5/4 generations) consistently hallucinate
  // "kitchen-permit-california" when the canonical slug uses "-in-".
  // Drops 550 potential 404s into one rule. Validates both project
  // and state slugs so we don't silently redirect garbage paths.
  {
    regex: /^\/permit-cost\/([a-z][a-z-]+)-permit-([a-z][a-z-]+)$/i,
    build: (m) => {
      const projectSlug = m[1].toLowerCase();
      const stateSlug = m[2].toLowerCase();
      if (!getStateBySlug(stateSlug)) return null;
      // Accept any reasonable project token; the downstream
      // PermitCostStateProjectPage will render its own 404 if the
      // project isn't recognized — better signal than silent rewrite.
      return `/permit-cost/${projectSlug}-in-${stateSlug}`;
    },
    meta: { kind: "permit-slug-correction", from: "permit-cost/{X}-permit-{state}", to: "permit-cost/{X}-in-{state}" },
  },
  // /state/{2-letter-code} → /state-overview/{full-state-slug}
  // Inbound links (Pinterest pins, syndicated cards, AI tools) frequently
  // guess the short `/state/ak`-style shape. Convert valid USPS codes to
  // our canonical slug; invalid codes fall through to the 404 page.
  // Discovered 2026-06: /state/ak alone had 8 hits across 4 days.
  {
    regex: /^\/state\/([a-z]{2})$/i,
    build: (m) => {
      const code = m[1].toUpperCase();
      const st = getStateByCode(code);
      return st ? `/state-overview/${st.slug}` : null;
    },
    meta: { kind: "state-code-shortform", from: "/state/{code}", to: "/state-overview/{slug}" },
  },
  // /detroitchicago/* — Ezoic CDN namespace residue. When the property
  // was previously connected via Ezoic, requests to /detroitchicago/erjs,
  // /detroitchicago/ezren, etc. were proxied to Ezoic's ad-serving CDN.
  // After we disconnected (Ezoic rejected the property, scripts commented
  // out in index.html 2026-02), external bots/cached pages still probe
  // these paths against our origin → 8+ hits over 3 days for one path.
  // Catch-all rule: silently absorb to the homepage so they neither 404
  // the user nor pollute our /admin 404 telemetry going forward.
  {
    regex: /^\/detroitchicago\/[a-z0-9_-]+$/i,
    build: () => "/",
    meta: { kind: "ezoic-namespace-absorb", from: "/detroitchicago/*", to: "/" },
  },
];

/**
 * STATIC_PATH_REDIRECTS — exact-path one-offs that don't fit any pattern.
 * Each key is a literal pathname (no trailing slash, lowercase) and each
 * value is the canonical target. Faster than scanning regex arrays and
 * lets us close common typos / legacy URLs without touching the admin DB.
 *
 * Discovered 2026-06 via /admin/not-found-reports top-hits:
 *   - /privacy.html (10 hits, 7 days) — legacy WordPress-era URL shape.
 *   - /renovation-timeline-benchmarks (9 hits) — typo'd internal link
 *     from answers-data.js (since fixed) + stale outbound shares.
 *   - /roof-claim-payout-calculator (8 hits) — shorter AI-guessed slug
 *     for /will-my-roof-claim-pay-out-calculator.
 *   - /basement-finish-cost-calculator (8 hits) — "finish" vs canonical
 *     "finishing".
 *   - /deck-roi-by-state (8 hits) — AI substitutes "roi" for "cost".
 */
export const STATIC_PATH_REDIRECTS = {
  "/privacy.html": "/privacy",
  "/renovation-timeline-benchmarks": "/renovation-timeline",
  "/roof-claim-payout-calculator": "/will-my-roof-claim-pay-out-calculator",
  "/basement-finish-cost-calculator": "/basement-finishing-cost-calculator",
  "/deck-roi-by-state": "/deck-cost-by-state",
  // 2026-06-25 second wave — discovered after the first STATIC_PATH_REDIRECTS
  // batch shipped. Same "AI-guessed shorter slug" pattern for pool ("in-ground-
  // pool" rather than canonical "pool-installation"), plus two hallucinated
  // guide slugs that no editor ever wrote — send them to the closest existing
  // canonical page so the user still lands on relevant content.
  "/in-ground-pool-cost-calculator": "/pool-installation-cost-calculator",
  "/guides/how-much-does-a-basement-addition-cost-2026": "/basement-finishing-cost-calculator",
  "/guides/storm-surge-roof-claim-florida-2026":
    "/guides/hail-damage-roof-replacement-cost-florida-2026",
  // 2026-02-20 — dead internal link from the 3 pre-listing matchup guides
  // (they had all referenced "the complete pre-listing decision playbook"
  // at /guides/pre-sale-fixes-vs-skip-2026, a guide that was never built).
  // The new pillar guide /guides/best-roi-renovations-before-selling-your-home-2026
  // fills exactly this content gap.
  "/guides/pre-sale-fixes-vs-skip-2026":
    "/guides/best-roi-renovations-before-selling-your-home-2026",
  // 2026-02-24 — spotted via GSC manual-index rejection. User typed the short
  // slug but the canonical route uses the full descriptive one. Preserve any
  // inbound Pinterest / social / backlink traffic to the short slug.
  "/pre-listing-spend-calculator":
    "/how-much-to-spend-fixing-up-before-listing-calculator",
};

/**
 * Look up a redirect target for a given path. Three-stage lookup:
 *   1. Exact-match the DB-loaded admin-managed redirect table.
 *   2. Pattern-match the static PATTERN_REDIRECTS list (AI-hallucination
 *      catch-alls that handle hundreds of paths from one rule).
 *   3. Path-pattern match the PATH_PATTERN_REDIRECTS list (segment-shape
 *      hallucinations like /permit-cost/{X}-permit-{state}).
 *
 * Exact-match wins over pattern-match so admins can override programmatic
 * canonicalization on a per-URL basis.
 */
export function findRedirect(path, redirects) {
  if (!path) return null;
  if (Array.isArray(redirects)) {
    const hit = redirects.find((r) => r.from_path === path);
    if (hit) return hit.to_path;
  }
  // Normalize once: lowercase + strip trailing slashes so /Privacy.HTML/,
  // /state/AK/, and /State/Ak all resolve through the same code paths.
  // We feed the same normalized key into both the static map and the
  // regex loops below so case + trailing-slash variants behave
  // identically across all three lookup tiers.
  const key = path.toLowerCase().replace(/\/+$/, "") || "/";
  if (STATIC_PATH_REDIRECTS[key]) return STATIC_PATH_REDIRECTS[key];
  for (const { regex, build } of PATTERN_REDIRECTS) {
    const m = key.match(regex);
    if (m) {
      const target = build(m);
      if (target && target !== key) return target;
    }
  }
  for (const { regex, build } of PATH_PATTERN_REDIRECTS) {
    const m = key.match(regex);
    if (m) {
      const target = build(m);
      if (target && target !== key) return target;
    }
  }
  return null;
}
